export const OUTBOUND_REALTIME_REQUIRED_MESSAGE = "请输入出库数量";
export const OUTBOUND_REALTIME_POSITIVE_MESSAGE = "出库数量必须大于0";
export const OUTBOUND_REALTIME_OVER_INVENTORY_MESSAGE = "出库数量超出可出库数量";

export interface OutboundRealtimeGoodsRow {
    goodsId?: number | string;
    goodsName?: string;
    model?: string;
    outboundWeight?: number | string | null;
}

export interface OutboundRealtimeInventoryItem {
    goodsId?: number | string;
    goodsName?: string;
    model?: string;
    periodEndInventory?: number | string | null;
}

export interface ValidateOutboundRealtimeQuantityParams {
    row: OutboundRealtimeGoodsRow;
    rowIndex?: number;
    activeRowIndex?: number;
    requireActiveRow?: boolean;
    rows: OutboundRealtimeGoodsRow[];
    inventoryItems?: OutboundRealtimeInventoryItem[];
}

export interface EditableOutboundRealtimeInventoryItemsParams {
    inventoryItems?: OutboundRealtimeInventoryItem[];
    originalRows?: OutboundRealtimeGoodsRow[];
    maxInventory?: number | string | null;
}

export const isEmptyOutboundWeight = (value: OutboundRealtimeGoodsRow["outboundWeight"]) => {
    return value === undefined || value === null || value === "";
};

const toFiniteNumber = (value: number | string | null | undefined) => {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : undefined;
};

const isSameGoodsModel = (left: OutboundRealtimeGoodsRow, right: OutboundRealtimeGoodsRow | OutboundRealtimeInventoryItem) => {
    // 同时存在 goodsId 时优先用 goodsId + model 匹配，否则回落到 goodsName + model。
    if (left.goodsId !== undefined && right.goodsId !== undefined) {
        return String(left.goodsId) === String(right.goodsId) && (left.model || "") === (right.model || "");
    }
    return (left.goodsName || "") === (right.goodsName || "") && (left.model || "") === (right.model || "");
};

const getSameGoodsModelTotal = (row: OutboundRealtimeGoodsRow, rows: OutboundRealtimeGoodsRow[]) => {
    return rows.reduce((total, currentRow) => {
        if (!isSameGoodsModel(row, currentRow)) return total;
        const currentWeight = toFiniteNumber(currentRow.outboundWeight);
        return total + (currentWeight || 0);
    }, 0);
};

/**
 * 编辑出库单时，库存明细可能是扣除当前单后的剩余量；校验前按原明细数量回补成可编辑上限。
 */
export const getEditableOutboundRealtimeInventoryItems = ({
    inventoryItems = [],
    originalRows = [],
    maxInventory
}: EditableOutboundRealtimeInventoryItemsParams) => {
    const maxInventoryValue = toFiniteNumber(maxInventory);
    return inventoryItems.map(item => {
        const periodEndInventory = toFiniteNumber(item.periodEndInventory);
        if (periodEndInventory === undefined) return item;

        const originalOutboundWeight = getSameGoodsModelTotal(item, originalRows);
        if (originalOutboundWeight <= 0) return item;

        const expandedInventory = periodEndInventory + originalOutboundWeight;
        const editableInventory =
            maxInventoryValue === undefined
                ? expandedInventory
                : Math.max(periodEndInventory, Math.min(expandedInventory, maxInventoryValue));
        return { ...item, periodEndInventory: editableInventory };
    });
};

/**
 * 校验出库明细行数量，并按后端提交兜底规则提前返回实时提示文案。
 */
export const validateOutboundRealtimeQuantity = ({
    row,
    rowIndex,
    activeRowIndex,
    requireActiveRow = false,
    rows,
    inventoryItems = []
}: ValidateOutboundRealtimeQuantityParams) => {
    if (isEmptyOutboundWeight(row.outboundWeight)) return "";

    const outboundWeight = toFiniteNumber(row.outboundWeight);
    if (outboundWeight === undefined || outboundWeight <= 0) return OUTBOUND_REALTIME_POSITIVE_MESSAGE;

    const inventoryItem = inventoryItems.find(item => isSameGoodsModel(row, item));
    const periodEndInventory = toFiniteNumber(inventoryItem?.periodEndInventory);
    if (periodEndInventory === undefined) return "";

    const totalOutboundWeight = getSameGoodsModelTotal(row, rows);
    if (totalOutboundWeight > periodEndInventory) {
        if (requireActiveRow && (activeRowIndex === undefined || rowIndex === undefined)) return "";
        if (activeRowIndex !== undefined && rowIndex !== undefined && activeRowIndex !== rowIndex) return "";
        return OUTBOUND_REALTIME_OVER_INVENTORY_MESSAGE;
    }

    return "";
};
